import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { getPageableAsParams, Page, Pageable } from '../models/page.model';
import { ICashRegister } from '../models/cash-register.model';
import {IAccount} from "../models/account.model";

@Injectable({
  providedIn: 'root'
})
export class CashRegisterService {

  private apiUrl = `operation/v1/cash-register`;

  constructor(private http: HttpClient) { }

  /**
   * Create a new cash register.
   * @param cashRegister The cash register to create.
   * @returns The created ICashRegister.
   */
  createCashRegister(cashRegister: ICashRegister): Observable<ICashRegister> {
    return this.http.post<ICashRegister>(this.apiUrl, cashRegister);
  }

  /**
   * Update an existing cash register by its id.
   * @param id The id of the cash register to update.
   * @param cashRegister The updated ICashRegister.
   * @returns The updated ICashRegister.
   */
  updateCashRegister(id: string, cashRegister: ICashRegister): Observable<ICashRegister> {
    return this.http.put<ICashRegister>(`${this.apiUrl}/${id}`, cashRegister);
  }

  /**
   * Get a specific cash register by its id.
   * @param id The id of the cash register.
   * @returns The ICashRegister.
   */
  getCashRegisterById(id: string): Observable<ICashRegister> {
    return this.http.get<ICashRegister>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get all cash registers with pagination.
   * @param pageable The parameter of the requested page.
   * @returns A page of ICashRegister.
   */
  getAllCashRegisters(pageable: Pageable): Observable<Page<ICashRegister>> {
    const params = getPageableAsParams(pageable);
    return this.http.get<Page<ICashRegister>>(this.apiUrl, { params });
  }

  /**
   * Get cash registers by institution with pagination.
   * @param institutionId The institution id.
   * @param pageable The parameter of the requested page.
   * @returns A page of ICashRegister.
   */
  getCashRegistersByInstitution(institutionId: string, pageable: Pageable): Observable<Page<ICashRegister>> {
    const params = getPageableAsParams(pageable);
    return this.http.get<Page<ICashRegister>>(`${this.apiUrl}/institution/${institutionId}`, { params });
  }

  /**
   * Get active cash registers for an institution.
   * @param institutionId The institution id.
   * @returns List of active ICashRegister.
   */
  getActiveCashRegisters(institutionId: string): Observable<ICashRegister[]> {
    return this.http.get<ICashRegister[]>(`${this.apiUrl}/institution/${institutionId}/active`);
  }

  /**
   * Delete a cash register.
   * @param id The id of the cash register to delete.
   * @returns Observable<void>
   */
  deleteCashRegister(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Activate a cash register.
   * @param id The id of the cash register.
   * @returns Observable<void>
   */
  activateCashRegister(id: string): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/activate`, {});
  }

  /**
   * Close a cash register.
   * @param id The id of the cash register.
   * @returns Observable<void>
   */
  closeCashRegister(id: string): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/close`, {});
  }

  /**
   * Set maintenance mode for a cash register.
   * @param id The id of the cash register.
   * @returns Observable<void>
   */
  setMaintenanceMode(id: string): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/maintenance`, {});
  }

  /**
   * get cash registers based on criteria
   * @param criteria the criteria
   * @param pageable The parameter of requested page
   */
  searchCashRegisterByMultipleCriteria(criteria: any, pageable: Pageable): Observable<Page<ICashRegister>> {
    let params = getPageableAsParams(pageable);
    params = params.set("institutionId", criteria.institution ?? '');
    params = params.set("type", criteria.type ?? '');
    params = params.set("status", criteria.status ?? '');
    return this.http.get<Page<ICashRegister>>(`${this.apiUrl}/searchByMultipleCriteria`, { params })
  }
}
